import type { AudioManager } from '../../audio/AudioManager';
import type { TouchInput } from '../../input/TouchInput';
import { LOGICAL_HEIGHT, LOGICAL_WIDTH } from '../../render/Renderer';
import { drawButton, pointInRect, roundRect } from '../../render/ui/UI';
import type { Rect, RunState, RunUpgradeDef } from '../../types';
import type { Phase } from '../Phase';
import { generateShopOffers } from '../systems/ShopGen';

const SKIP_BUTTON: Rect = { x: 90, y: 568, w: LOGICAL_WIDTH - 180, h: 44 };

interface OfferSlot {
  def: RunUpgradeDef;
  rect: Rect;
}

export class ShopPhase implements Phase {
  private run: RunState;
  private audio: AudioManager;
  private offers: OfferSlot[];
  private done = false;
  private unbind: (() => void) | null = null;
  private elapsed = 0;
  private selectedId: string | null = null;
  private skipped = false;

  constructor(run: RunState, input: TouchInput, audio: AudioManager) {
    this.run = run;
    this.audio = audio;
    const ownedIds = run.upgrades.map((u) => u.id);
    const defs = generateShopOffers(ownedIds, 3);
    this.offers = defs.map((def, i) => ({
      def,
      rect: { x: 24, y: 110 + i * 130, w: LOGICAL_WIDTH - 48, h: 110 },
    }));
    this.unbind = input.onPointerDown((e) => this.handleDown(e));
  }

  private handleDown(e: { pos: { x: number; y: number } }): void {
    if (this.done) return;
    if (pointInRect(e.pos.x, e.pos.y, SKIP_BUTTON)) {
      this.audio.play('bellows_tap');
      this.skipped = true;
      this.done = true;
      return;
    }
    for (const slot of this.offers) {
      if (pointInRect(e.pos.x, e.pos.y, slot.rect)) {
        if (this.run.gold >= slot.def.cost) {
          this.run.gold -= slot.def.cost;
          this.run.upgrades.push({ id: slot.def.id, name: slot.def.name });
          this.selectedId = slot.def.id;
          this.audio.play('weapon_complete');
          this.done = true;
        } else {
          this.audio.play('strike_miss');
        }
        return;
      }
    }
  }

  update(dt: number): void {
    this.elapsed += dt;
  }

  isComplete(): boolean {
    return this.done;
  }

  /** Returns the upgrade id the player picked, or null if they skipped. */
  getPickedId(): string | null {
    return this.skipped ? null : this.selectedId;
  }

  dispose(): void {
    this.unbind?.();
    this.unbind = null;
  }

  render(ctx: CanvasRenderingContext2D): void {
    const grad = ctx.createLinearGradient(0, 0, 0, LOGICAL_HEIGHT);
    grad.addColorStop(0, '#1a1208');
    grad.addColorStop(1, '#0a0805');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, LOGICAL_WIDTH, LOGICAL_HEIGHT);

    ctx.fillStyle = '#f0d890';
    ctx.font = 'bold 22px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('The Smithy Stall', LOGICAL_WIDTH / 2, 28);
    ctx.fillStyle = '#a8957a';
    ctx.font = '12px system-ui, sans-serif';
    ctx.fillText(`Gold: ${this.run.gold}`, LOGICAL_WIDTH / 2, 60);
    ctx.fillText(`Pick one offer, or skip.`, LOGICAL_WIDTH / 2, 78);

    for (const slot of this.offers) {
      const canAfford = this.run.gold >= slot.def.cost;
      ctx.fillStyle = canAfford ? '#2a2420' : '#1a1714';
      roundRect(ctx, slot.rect.x, slot.rect.y, slot.rect.w, slot.rect.h, 8);
      ctx.fill();
      ctx.strokeStyle = canAfford ? '#7a6a55' : '#3a3530';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.fillStyle = canAfford ? '#f0d890' : '#7a6a55';
      ctx.font = 'bold 16px system-ui, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText(slot.def.name, slot.rect.x + 14, slot.rect.y + 14);

      ctx.fillStyle = canAfford ? '#c9b896' : '#5a5045';
      ctx.font = '12px system-ui, sans-serif';
      wrapText(ctx, slot.def.description, slot.rect.x + 14, slot.rect.y + 40, slot.rect.w - 28, 14);

      ctx.fillStyle = canAfford ? '#ffd84a' : '#7a6a55';
      ctx.font = 'bold 14px system-ui, sans-serif';
      ctx.textAlign = 'right';
      ctx.fillText(`${slot.def.cost}g`, slot.rect.x + slot.rect.w - 14, slot.rect.y + 14);
    }

    drawButton(ctx, {
      rect: SKIP_BUTTON,
      label: 'SKIP (free)',
      enabled: true,
      highlight: false,
    });
  }
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): void {
  const words = text.split(' ');
  let line = '';
  let curY = y;
  for (const w of words) {
    const test = line ? line + ' ' + w : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, curY);
      line = w;
      curY += lineHeight;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, curY);
}
